import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import QuickView from "@/components/QuickView";
import Reveal from "@/components/Reveal";

const TAGS = [
  { id: "all", label: "All 12" },
  { id: "flavour", label: "Flavour" },
  { id: "superfood", label: "Superfood" },
  { id: "immunity", label: "Immunity" },
  { id: "energy", label: "Energy" },
];

export default function Shop() {
  const [tag, setTag] = useState("all");
  const [q, setQ] = useState("");
  const [quick, setQuick] = useState(null);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await api.get("/products")).data,
  });

  const filtered = useMemo(() => {
    let list = products;
    if (tag !== "all") list = list.filter((p) => p.tags.includes(tag));
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((p) => `${p.name} ${p.mascot} ${p.benefit}`.toLowerCase().includes(s));
    }
    return list;
  }, [products, tag, q]);

  return (
    <div className="mx-auto max-w-7xl px-4 pb-24 pt-28 sm:px-6" data-testid="shop-page">
      <Reveal>
        <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-leaf">The collection</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-forest sm:text-6xl">
          THE WHOLE SQUAD.
        </h1>
        <p className="mt-3 max-w-lg text-sm text-ink/60 sm:text-base">
          Every pouch: 100 g, one ingredient, zero nonsense. Pick your players.
        </p>
      </Reveal>

      <Reveal delay={0.08}>
        <div className="mt-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar" data-testid="shop-tag-filters">
            {TAGS.map((t) => (
              <button
                key={t.id}
                data-testid={`shop-filter-${t.id}`}
                onClick={() => setTag(t.id)}
                className={`shrink-0 rounded-full px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider transition-all ${
                  tag === t.id ? "bg-forest text-cream" : "border border-sand bg-white text-forest hover:border-leaf hover:text-leaf"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative md:w-72">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
            <input
              data-testid="shop-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search powders..."
              className="w-full rounded-full border border-sand bg-white py-3 pl-11 pr-4 text-sm font-semibold text-ink placeholder:text-ink/40 focus:border-leaf"
            />
          </div>
        </div>
      </Reveal>

      {isLoading ? (
        <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] animate-pulse rounded-3xl bg-sand/50" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-16 text-center">
          <p className="font-display text-3xl font-extrabold text-forest">No powders matched.</p>
          <p className="mt-2 text-sm text-ink/60">Try another word — or meet all 12 again.</p>
          <button data-testid="shop-clear-search" onClick={() => { setQ(""); setTag("all"); }} className="mt-6 rounded-full bg-forest px-6 py-3 text-sm font-bold text-cream hover:bg-leaf">
            Clear filters
          </button>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} onQuickView={setQuick} />
          ))}
        </div>
      )}

      <QuickView product={quick} onClose={() => setQuick(null)} />
    </div>
  );
}
